import { Plus, Trash2 } from 'lucide-react';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { Button, Form, Modal, Spinner } from 'react-bootstrap';

const getEmptyHoliday = () => ({
  holidayName: '',
  startDate: '',
  endDate: '',
  notes: ''
});

const rowHasAnyField = holiday =>
  Boolean(
    (holiday.holidayName && holiday.holidayName.trim()) ||
      holiday.startDate ||
      holiday.endDate ||
      (holiday.notes && holiday.notes.trim())
  );

const AddHolidayModal = ({ show, onHide, showProgress, onSubmit, edit }) => {
  const [holidays, setHolidays] = useState([getEmptyHoliday()]);
  const [errors, setErrors] = useState({});

  const errorKey = (index, field) => `${index}-${field}`;

  const handleInputChange = (index, field, value) => {
    setHolidays(prev =>
      prev.map((holiday, idx) =>
        idx === index
          ? {
              ...holiday,
              [field]: value
            }
          : holiday
      )
    );
    const key = errorKey(index, field);
    setErrors(prev => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleAddHolidayRow = () => {
    setHolidays(prev => [...prev, getEmptyHoliday()]);
    setErrors({});
  };

  const handleRemoveHolidayRow = index => {
    setHolidays(prev => prev.filter((_, idx) => idx !== index));
    setErrors({});
  };

  const isEditMode = Boolean(edit?._id);

  const validateForm = () => {
    const newErrors = {};

    if (isEditMode) {
      const h = holidays[0] || getEmptyHoliday();
      if (!h.holidayName || h.holidayName.trim() === '') {
        newErrors[errorKey(0, 'holidayName')] = 'Holiday name is required';
      }
      if (!h.startDate) {
        newErrors[errorKey(0, 'startDate')] = 'Start date is required';
      }
      if (h.startDate && h.endDate && h.endDate < h.startDate) {
        newErrors[errorKey(0, 'endDate')] = 'End date must be on or after start date';
      }
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    }

    let hasOneCompleteRow = false;
    holidays.forEach((h, i) => {
      const partial = rowHasAnyField(h);
      const complete = (h.holidayName || '').trim() && h.startDate;
      if (complete) hasOneCompleteRow = true;
      if (!partial) return;
      if (!h.holidayName || h.holidayName.trim() === '') {
        newErrors[errorKey(i, 'holidayName')] = 'Holiday name is required';
      }
      if (!h.startDate) {
        newErrors[errorKey(i, 'startDate')] = 'Start date is required';
      }
      if (h.startDate && h.endDate && h.endDate < h.startDate) {
        newErrors[errorKey(i, 'endDate')] = 'End date must be on or after start date';
      }
    });

    if (!hasOneCompleteRow) {
      newErrors[errorKey(0, 'holidayName')] = newErrors[errorKey(0, 'holidayName')] || 'Holiday name is required';
      newErrors[errorKey(0, 'startDate')] = newErrors[errorKey(0, 'startDate')] || 'Start date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = e => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    if (isEditMode) {
      onSubmit(holidays[0]);
      return;
    }

    const cleanedHolidays = holidays
      .map(holiday => ({
        holidayName: holiday.holidayName.trim(),
        startDate: holiday.startDate,
        endDate: holiday.endDate,
        notes: holiday.notes
      }))
      .filter(holiday => holiday.holidayName || holiday.startDate || holiday.endDate || holiday.notes);

    onSubmit(cleanedHolidays);
  };

  useEffect(() => {
    if (!show) {
      setErrors({});
      return;
    }

    if (edit?._id) {
      setHolidays([
        {
          holidayName: edit.holidayName || '',
          startDate: edit.startDate ? moment(edit.startDate).format('YYYY-MM-DD') : '',
          endDate: edit.endDate ? moment(edit.endDate).format('YYYY-MM-DD') : '',
          notes: edit.notes || ''
        }
      ]);
      setErrors({});
      return;
    }

    setHolidays([getEmptyHoliday()]);
    setErrors({});
  }, [edit, show]);

  return (
    <Modal size="lg" show={show} onHide={onHide} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{isEditMode ? 'Edit Holiday' : 'Add Holiday'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          {holidays.map((holiday, index) => (
            <div key={`holiday-row-${index}`} className={index ? 'pt-3 mt-4 border-top border-gray' : ''}>
              {!isEditMode && index > 0 && (
                <div className="d-flex justify-content-end align-items-center">
                  <Button variant="danger" size="sm" onClick={() => handleRemoveHolidayRow(index)}>
                    <Trash2 size={16} />
                  </Button>
                </div>
              )}
              <div className="d-flex justify-content-evenly" style={{ gap: 12 }}>
              <Form.Group className="w-100">
                <Form.Label>
                  Holiday Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g., Thanksgiving"
                  value={holiday.holidayName}
                  className={errors[errorKey(index, 'holidayName')] ? 'is-invalid' : ''}
                  onChange={e => handleInputChange(index, 'holidayName', e.target.value)}
                />
                {errors[errorKey(index, 'holidayName')] && (
                  <div className="text-danger small mt-1">{errors[errorKey(index, 'holidayName')]}</div>
                )}
              </Form.Group>
                <Form.Group className="w-50">
                  <Form.Label>
                    Start Date <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="date"
                    value={holiday.startDate}
                    className={errors[errorKey(index, 'startDate')] ? 'is-invalid' : ''}
                    onChange={e => handleInputChange(index, 'startDate', e.target.value)}
                  />
                  {errors[errorKey(index, 'startDate')] && (
                    <div className="text-danger small mt-1">{errors[errorKey(index, 'startDate')]}</div>
                  )}
                </Form.Group>
                <Form.Group className="w-50">
                  <Form.Label>End Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={holiday.endDate}
                    className={errors[errorKey(index, 'endDate')] ? 'is-invalid' : ''}
                    onChange={e => handleInputChange(index, 'endDate', e.target.value)}
                  />
                  {errors[errorKey(index, 'endDate')] && (
                    <div className="text-danger small mt-1">{errors[errorKey(index, 'endDate')]}</div>
                  )}
                </Form.Group>
              </div>
              <Form.Group>
                <Form.Label>Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="Optional notes about this holiday"
                  value={holiday.notes}
                  onChange={e => handleInputChange(index, 'notes', e.target.value)}
                />
              </Form.Group>
            </div>
          ))}
          {!isEditMode && (
            <div className="d-flex my-2">
              <button
                type="button"
                disabled={showProgress}
                onClick={handleAddHolidayRow}
                className="d-flex align-items-center bg-transparent hover-muted text-muted hover-text-dark"
                style={{ 
                  padding: '7px 16px',
                  border: '1px dashed  grey',
                  borderRadius: '10px',
                  transition: 'all 0.1s ease',
                }}
              >
                <Plus size={16} className="mr-1" />
                <span>Add Holiday</span>
              </button>
            </div>
          )}
          <div className="d-flex justify-content-end">
            <Button
              variant="light"
              className="border d-flex align-items-center justify-content-center px-2 mr-2"
              title="More"
              disabled={showProgress}
              onClick={onHide}
            >
              Cancel
            </Button>
            <Button
              variant="success"
              type="submit"
              className="d-inline-flex align-items-center justify-content-center"
              disabled={showProgress}
            >
              {showProgress && <Spinner size="sm" animation="border" className="mr-1" />}
              {isEditMode ? 'Update' : 'Submit'}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default AddHolidayModal;
